import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { Observable, BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TemplateCategoriesService, TemplateCategory } from '../template-categories.service';
import { CategoryAssignmentDialogComponent, CategoryAssignmentDialogData } from '../category-assignment-dialog/category-assignment-dialog.component';

export interface TemplateOrganizationBrowserData {
  directie_id?: number;
  compartiment_id?: number;
}

interface TemplateItem {
  id: number;
  name: string;
  type: 'oficial' | 'cerere';
  active: boolean;
  categories: TemplateCategory[];
  primary_category?: TemplateCategory;
  directie_info?: any;
  compartiment_info?: any;
}

@Component({
  selector: 'app-template-organization-browser',
  templateUrl: './template-organization-browser.component.html',
  styleUrls: ['./template-organization-browser.component.scss']
})
export class TemplateOrganizationBrowserComponent implements OnInit {
  
  filterForm: FormGroup;
  isLoading = false;
  
  // Tree and category data
  categoryTree: TemplateCategory[] = [];
  selectedCategory: TemplateCategory | null = null;
  
  // Template data
  templates: TemplateItem[] = [];
  filteredTemplates: TemplateItem[] = [];
  templateTypes = [
    { value: '', label: 'Toate tipurile' },
    { value: 'oficial', label: 'Template-uri Oficiale' },
    { value: 'cerere', label: 'Template-uri Cereri' }
  ];
  
  // View settings
  viewMode: 'category' | 'all' = 'category';
  showUncategorized = false;
  
  // Search
  searchTerm$ = new BehaviorSubject<string>('');
  
  // Statistics
  statistics: any = {};
  
  // Make Object available to template
  Object = Object;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<TemplateOrganizationBrowserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TemplateOrganizationBrowserData,
    private templateCategoriesService: TemplateCategoriesService,
    private dialog: MatDialog
  ) {
    this.filterForm = this.createForm();
  }

  ngOnInit(): void {
    this.setupSearch();
    this.loadCategoryTree();
    this.loadTemplates();
    this.loadStatistics();
  }

  createForm(): FormGroup {
    return this.fb.group({
      search: [''],
      template_type: [''],
      status: [''],
      category_filter: ['']
    });
  }

  setupSearch(): void {
    this.filterForm.get('search')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchTerm$.next(searchTerm || '');
      this.filterTemplates();
    });
    
    // Listen to other filter changes
    this.filterForm.get('template_type')?.valueChanges.subscribe(() => this.filterTemplates());
    this.filterForm.get('status')?.valueChanges.subscribe(() => this.filterTemplates());
  }

  loadCategoryTree(): void {
    this.templateCategoriesService.getTreeStructure(this.data.directie_id, this.data.compartiment_id)
      .subscribe({
        next: (response) => {
          if (response.errors === false) {
            this.categoryTree = response.data || [];
          }
        },
        error: (error) => {
          console.error('Error loading category tree:', error);
          this.categoryTree = [];
        }
      });
  }

  loadTemplates(): void {
    this.isLoading = true;
    
    this.templateCategoriesService.getTemplateTreeView({
      directie_id: this.data.directie_id,
      compartiment_id: this.data.compartiment_id,
      include_uncategorized: true
    }).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.errors === false) {
          this.processTemplateData(response.data);
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading templates:', error);
        this.templates = [];
        this.filteredTemplates = [];
      }
    });
  }

  processTemplateData(data: any): void {
    this.templates = [];
    
    // Process categorized templates
    if (data.categorized_templates) {
      data.categorized_templates.forEach((item: any) => {
        this.templates.push({
          id: item.id,
          name: item.name,
          type: item.type,
          active: item.active,
          categories: item.categories || [],
          primary_category: item.primary_category,
          directie_info: item.directie_info,
          compartiment_info: item.compartiment_info
        });
      });
    }
    
    // Process uncategorized templates
    if (data.uncategorized_templates) {
      data.uncategorized_templates.forEach((item: any) => {
        this.templates.push({
          id: item.id,
          name: item.name,
          type: item.type,
          active: item.active,
          categories: [],
          directie_info: item.directie_info,
          compartiment_info: item.compartiment_info
        });
      });
    }
    
    this.filterTemplates();
  }

  loadStatistics(): void {
    this.templateCategoriesService.getStatistics().subscribe({
      next: (response) => {
        if (response.errors === false) {
          this.statistics = response.data || {};
        }
      },
      error: (error) => {
        console.error('Error loading statistics:', error);
      }
    });
  }

  filterTemplates(): void {
    let filtered = [...this.templates];
    
    const searchTerm = this.filterForm.get('search')?.value?.toLowerCase() || '';
    const templateType = this.filterForm.get('template_type')?.value;
    const status = this.filterForm.get('status')?.value;
    
    // Text search
    if (searchTerm) {
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchTerm)
      );
    }
    
    // Template type filter
    if (templateType) {
      filtered = filtered.filter(template => template.type === templateType);
    }
    
    // Status filter
    if (status === 'active') {
      filtered = filtered.filter(template => template.active);
    } else if (status === 'inactive') {
      filtered = filtered.filter(template => !template.active);
    }
    
    // Category filter
    if (this.viewMode === 'category' && this.selectedCategory) {
      filtered = filtered.filter(template => 
        this.isTemplateInCategory(template, this.selectedCategory!.id)
      );
    } else if (this.showUncategorized) {
      filtered = filtered.filter(template => 
        template.categories.length === 0 && !template.primary_category
      );
    }
    
    this.filteredTemplates = filtered;
  }

  isTemplateInCategory(template: TemplateItem, categoryId: number): boolean {
    if (template.primary_category?.id === categoryId) {
      return true;
    }
    
    return template.categories.some(cat => cat.id === categoryId);
  }

  onCategorySelected(category: TemplateCategory): void {
    this.selectedCategory = category;
    this.viewMode = 'category';
    this.showUncategorized = false;
    this.filterTemplates();
  }

  onViewModeChange(mode: 'category' | 'all'): void {
    this.viewMode = mode;
    if (mode === 'all') {
      this.selectedCategory = null;
      this.showUncategorized = false;
    }
    this.filterTemplates();
  }

  showUncategorizedTemplates(): void {
    this.showUncategorized = true;
    this.viewMode = 'category';
    this.selectedCategory = null;
    this.filterTemplates();
  }

  openCategoryAssignmentDialog(template: TemplateItem): void {
    const dialogData: CategoryAssignmentDialogData = {
      templateId: template.id,
      templateType: template.type,
      templateName: template.name,
      currentCategories: template.categories,
      currentPrimaryCategory: template.primary_category,
      directieId: this.data.directie_id,
      compartimentId: this.data.compartiment_id
    };

    const dialogRef = this.dialog.open(CategoryAssignmentDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      height: '85vh',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        // Update the template with new category assignments
        const updatedTemplate = this.templates.find(t => t.id === template.id);
        if (updatedTemplate) {
          updatedTemplate.categories = result.assignedCategories || [];
          updatedTemplate.primary_category = result.primaryCategory;
        }
        
        this.filterTemplates();
      }
    });
  }

  getTemplateStatusClass(template: TemplateItem): string {
    const classes = ['template-item'];
    
    if (!template.active) {
      classes.push('inactive');
    }
    
    if (template.categories.length === 0 && !template.primary_category) {
      classes.push('uncategorized');
    }
    
    return classes.join(' ');
  }

  getTemplateTypeIcon(type: 'oficial' | 'cerere'): string {
    return type === 'oficial' ? 'description' : 'assignment';
  }

  getTemplateTypeLabel(type: 'oficial' | 'cerere'): string {
    return type === 'oficial' ? 'Document Oficial' : 'Template Cerere';
  }

  getCategoriesDisplay(template: TemplateItem): string {
    const categories = template.categories || [];
    
    if (categories.length === 0 && !template.primary_category) {
      return 'Necategorizat';
    }
    
    let display = '';
    
    if (template.primary_category) {
      display = `${template.primary_category.name} (primară)`;
      
      const otherCategories = categories.filter(c => c.id !== template.primary_category!.id);
      if (otherCategories.length > 0) {
        display += `, +${otherCategories.length} altele`;
      }
    } else if (categories.length > 0) {
      if (categories.length === 1) {
        display = categories[0].name;
      } else {
        display = `${categories[0].name}, +${categories.length - 1} altele`;
      }
    }
    
    return display;
  }

  getViewTitle(): string {
    if (this.showUncategorized) {
      return 'Template-uri Necategorizate';
    } else if (this.selectedCategory) {
      return `Categorie: ${this.selectedCategory.name}`;
    } else {
      return 'Toate Template-urile';
    }
  }

  getFilteredCount(): number {
    return this.filteredTemplates.length;
  }

  getTotalCount(): number {
    return this.templates.length;
  }

  refreshData(): void {
    this.loadTemplates();
    this.loadCategoryTree();
    this.loadStatistics();
  }

  onClose(): void {
    this.dialogRef.close({ refresh: false });
  }

  // Track by functions for performance
  trackByTemplate(index: number, template: TemplateItem): number {
    return template.id;
  }

  trackByCategory(index: number, category: TemplateCategory): number {
    return category.id;
  }
}